import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'

const linkVariants = cva(
  'inline-flex items-center gap-1 font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary-500 rounded-sm',
  {
    variants: {
      variant: {
        primary:
          'text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300',
        secondary:
          'text-gray-700 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white',
        muted:
          'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200',
        danger: 'text-red-600 hover:text-red-700 dark:text-red-400',
      },
      underline: {
        always: 'underline underline-offset-4',
        hover: 'underline-offset-4 hover:underline',
        none: 'no-underline',
      },
      size: {
        sm: 'text-xs',
        md: 'text-sm',
        lg: 'text-base',
      },
    },
    defaultVariants: { variant: 'primary', underline: 'hover', size: 'md' },
  }
)

export interface LinkProps
  extends
    React.AnchorHTMLAttributes<HTMLAnchorElement>,
    VariantProps<typeof linkVariants> {
  external?: boolean
}

export const Link = React.forwardRef<HTMLAnchorElement, LinkProps>(
  (
    {
      className,
      variant,
      underline,
      size,
      external,
      rel,
      target,
      children,
      ...rest
    },
    ref
  ) => {
    const isExternal = external ?? target === '_blank'
    return (
      <a
        ref={ref}
        target={target}
        rel={
          isExternal
            ? rel
              ? `${rel} noopener noreferrer`
              : 'noopener noreferrer'
            : rel
        }
        className={cn(linkVariants({ variant, underline, size, className }))}
        {...rest}
      >
        {children}
      </a>
    )
  }
)

Link.displayName = 'Link'

export { linkVariants }
